import { Mail, Res } from 'common/types/responses'
import { MailActionType } from 'common/types/enum'
import { Req } from 'common/types/requests'
import { service } from 'common/service'
import dayjs from 'dayjs'
import transformPaginatedResponse from './util/transformPaginatedResponse'
import { toParam } from '@hoxtonmix/utils'
import { parseMailStatus } from './mailUtils'
import { API } from 'project/api'

export const mailService = service
  .enhanceEndpoints({ addTagTypes: ['Mail'] })
  .injectEndpoints({
    endpoints: (builder) => ({
      getMail: builder.query<Res['mail'], Req['getMail']>({
        onQueryStarted: async (query, { dispatch, queryFulfilled }) => {
          const { data } = await queryFulfilled
          // Prefetch details only on mobile
          if (!API.isMobile()) return
          // Lazy import to avoid circular dependency with useMailItem
          const { mailItemService } = await import('./useMailItem')
          for (const mail of data.results) {
            dispatch(
              mailItemService.endpoints.getMailItem.initiate({
                id: mail.id,
              }),
            )
          }
        },
        providesTags: (res, _, req) => [
          { id: `LIST-${req.subscription_id}`, type: 'Mail' },
        ],
        query: (query) => ({
          url: `/mailbox/mails?${toParam(
            {
              ...query,
              q: query.q ? query.q : undefined,
            },
            { arrayFormat: 'repeat' },
          )}`,
        }),
        transformResponse: (
          res: Res['mail'],
          _req: any,
          query: Req['getMail'],
        ) => {
          const paginatedResponse = transformPaginatedResponse(
            {
              ...res,
              results: (res.results || []).map((v: Mail) => {
                return {
                  ...v,

                  actions: v.read
                    ? v.actions.concat([
                        // Add a faux action letting users mark as unread
                        {
                          action_type: MailActionType.MARK_AS_UNREAD,
                          price: 0,
                          requires_topup: false,
                        },
                      ])
                    : v.actions.concat([
                        // Add a faux action letting users mark as read
                        {
                          action_type: MailActionType.MARK_AS_READ,
                          price: 0,
                          requires_topup: false,
                        },
                      ]),
                  // Add frontend-computed fields
                  display_status: parseMailStatus(
                    v?.status,
                    v?.expiry,
                    v?.address || '',
                  ),
                  expired: new Date().valueOf() > new Date(v.expiry).valueOf(),
                  expiring: dayjs(v.expiry).diff(new Date(), 'days') < 7,
                }
              }) as Mail[],
            },
            query,
          )
          return paginatedResponse
        },
      }),
      // END OF ENDPOINTS
    }),
  })

// Re-export from mailUtils to preserve existing imports
export { parseMailStatus, formatStatus } from './mailUtils'

export async function getMail(
  store: any,
  data: Req['getMail'],
  options?: Parameters<typeof mailService.endpoints.getMail.initiate>[1],
) {
  return store.dispatch(mailService.endpoints.getMail.initiate(data, options))
}
// END OF FUNCTION_EXPORTS

export const {
  useGetMailQuery,
  // END OF EXPORTS
} = mailService

// const { data, isLoading } = useGetMailQuery({ id: 2 }, {}) get hook
// const [createMail, { isLoading, data, isSuccess }] = useCreateMailMutation() create hook
// mailService.endpoints.getMail.select({id: 2})(store.getState()) access data from any function

export const getActionDisplayName = (mailAction: MailActionType) => {
  switch (mailAction) {
    case MailActionType.SCAN:
      return 'Scan Mail'
    case MailActionType.DOWNLOAD:
      return 'Download Mail'
    case MailActionType.FORWARD:
      return 'Forward Mail'
    default:
      ''
  }
}

export function getDestructionPolicyMessage(exipiryDate: string) {
  const expiry = dayjs(exipiryDate)

  const policyData = `This mail item will be securely destroyed on <strong>${expiry.format(
    'Do MMMM',
  )}</strong>. If you need a record of this mail item, collect or request a physical copy`
  return policyData
}
